import React from 'react';

/**
* closest
* @param {Element} el Element to traverse up from
* @param {String} css A selector to search for matching parents or element itself
* @return {Element|null}  Element which is the closest ancestor matching selector
*/
var closest = (function () {
  var proto = typeof window === 'undefined' ? {} : window.Element.prototype;
  var match = proto.matches || proto.msMatchesSelector || proto.webkitMatchesSelector;
  return proto.closest ? function (el, css) { return el.closest(css); } : function (el, css) {
    for (;el; el = el.parentElement) { if (match.call(el, css)) { return el } }
    return null
  }
})();

/**
* customElementToReact
* @param {Class|Function} elementClass A custom element definition.
* @param {Array} options Props and custom events
* @return {Object} A React component
*/
function customElementToReact (elementClass, options) {
  if ( options === void 0 ) options = {};

  var name = elementClass.name || String(elementClass).match(/function ([^(]+)/)[1]; // String match for IE11
  var dashCase = name.replace(/.[A-Z]/g, function (ref) {
    var a = ref[0];
    var b = ref[1];

    return (a + "-" + b);
  }); // NameName -> name-name
  var customProps = options.props || [];
  var customEvents = options.customEvents || [];
  var eventMap = customEvents.reduce(function (map, eventName) {
    map[eventName] = "on" + (eventName.replace(/(^|\.)./g, function (m) { return m.slice(-1).toUpperCase(); })); // input.filter => onInputFilter
    return map
  }, {});
  var skipProps = customProps.concat('forwardRef', Object.keys(eventMap).map(function (onEventName) { return eventMap[onEventName]; }));
  var tagName = (dashCase + "-" + (options.suffix || 'react')).replace(/\W+/g, '-').toLowerCase();

  return /*@__PURE__*/(function (superclass) {
    function anonymous (props) {
      var this$1 = this;

      superclass.call(this, props);
      // Register ref prop for accessing custom element https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
      this.ref = function (el) {
        if (typeof this$1.props.forwardRef === 'function') { this$1.props.forwardRef(el); }
        else if (this$1.props.forwardRef) { this$1.props.forwardRef.current = el; }
        return (this$1.el = el)
      };
      // Register event handler on component for each custom event
      Object.keys(eventMap).forEach(function (eventName) {
        var onEventName = eventMap[eventName];
        this$1[eventName] = function (event) {
          if (this$1.props[onEventName] && closest(event.target, this$1.el.nodeName) === this$1.el) {
            this$1.props[onEventName](event);
          }
        };
      });
    }

    if ( superclass ) anonymous.__proto__ = superclass;
    anonymous.prototype = Object.create( superclass && superclass.prototype );
    anonymous.prototype.constructor = anonymous;

    anonymous.prototype.componentDidMount = function componentDidMount () {
      var this$1 = this;

      // Run connectedCallback() after React componentDidMount() to allow React hydration to run first
      if (!window.customElements.get(tagName)) {
        window.customElements.define(tagName, elementClass);
      }

      // Populate properties on custom element
      customProps.forEach(function (propName) {
        if (propName in this$1.props) {
          this$1.el[propName] = this$1.props[propName];
        }
      });

      // Register events on custom element
      customEvents.forEach(function (eventName) {
        this$1.el.addEventListener(eventName, this$1[eventName]);
      });
    };

    anonymous.prototype.componentDidUpdate = function componentDidUpdate (prev) {
      var this$1 = this;

      // Sync prop changes to custom element
      customProps.forEach(function (propName) {
        if (prev[propName] !== this$1.props[propName]) {
          this$1.el[propName] = this$1.props[propName];
        }
      });
    };

    anonymous.prototype.componentWillUnmount = function componentWillUnmount () {
      var this$1 = this;

      // Remove event handlers on custom element on unmount
      customEvents.forEach(function (eventName) {
        this$1.el.removeEventListener(eventName, this$1[eventName]);
      });
    };

    anonymous.prototype.render = function render () {
      var this$1 = this;

      // Convert React props to CustomElement props https://github.com/facebook/react/issues/12810
      return React.createElement(tagName, Object.keys(this.props).reduce(function (thisProps, propName) {
        if (skipProps.indexOf(propName) === -1) { // Do not render customEvents and custom props as attributes
          if (propName === 'className') { thisProps.class = this$1.props[propName]; } // Fixes className for custom elements
          else if (this$1.props[propName] === true) { thisProps[propName] = ''; } // Fixes boolean attributes
          else if (this$1.props[propName] !== false) { thisProps[propName] = this$1.props[propName]; } // Pass only truthy, non-function props
        }
        return thisProps
      }, { ref: this.ref }))
    };

    return anonymous;
  }(React.Component))
}

export default customElementToReact;
